import { Form, Formik, FormikHelpers } from 'formik';
import { motion } from 'framer-motion';
import { GoSkipFill } from 'react-icons/go';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { Rating } from 'react-simple-star-rating';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useAssociateFeedbackActions from '../../../hooks/APIHooks/appointments/useAssociateFeedbackActions';
import useClientFeedbackActions from '../../../hooks/APIHooks/appointments/useClientFeedbackActions';
import {
  associateFeedbackSchema,
  clientFeedbackSchema,
} from '../../../schemas';
import {
  AssociateFeedbackBody,
  ClientFeedbackBody,
} from '../../../types/feedbacks';
import { ParticipantData } from '../../../types/session';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import SkillBadge from '../Badges/SkillBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaRadioField from '../Inputs/AcademiaRadioField';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const FeedbackModal = ({
  localUser,
  remoteUser,
}: {
  localUser: ParticipantData;
  remoteUser: ParticipantData;
}) => {
  // integration of context hooks here
  const { currentUserType, currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { createAssociateFeedbackHandler } = useAssociateFeedbackActions('');
  const { createClientFeedbackHandler } = useClientFeedbackActions('');

  // integration of react-router-dom hooks here
  const navigate = useNavigate();

  // handler function to create new feedback declared here
  const createFeedbackHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values);

      try {
        if (currentUserType === 'user') {
          const body: AssociateFeedbackBody = {
            appointment: localUser.appointment_request_id,
            associate: localUser.participant_id,
            client: remoteUser.participant_id,
            comments: values.comments,
            created_by: currentUser?.username as string,
          };

          console.log('ASSOCIATE FEEDBACK REQ BODY: ', body);

          await createAssociateFeedbackHandler
            .mutateAsync({ body })
            .then((res) => {
              if (res.is_success) {
                toast.success('Feedback Submitted!!');

                navigate('/appointment/list');
              } else {
                toast.error(res.message || 'Feedback Submission Failed!!');
              }
            })
            .catch((error) => toast.error(fetchErrorMessage(error)));
        } else {
          const body: ClientFeedbackBody = {
            appointment: localUser.appointment_request_id,
            client: localUser.participant_id,
            associate: remoteUser.participant_id,
            will_retain: values.retain_answer,
            rating: values.rating,
            comments: values.comments,
            created_by: currentUser?.username as string,
          };

          console.log('CLIENT FEEDBACK REQ BODY: ', body);

          await createClientFeedbackHandler
            .mutateAsync({ body })
            .then((res) => {
              if (res.is_success) {
                toast.success('Feedback Submitted!!');

                navigate('/appointment/list');
              } else {
                toast.error(res.message || 'Feedback Submission Failed!!');
              }
            })
            .catch((error) => toast.error(fetchErrorMessage(error)));
        }
      } catch (error) {
        toast.error('Something Went Wrong!!');
      } finally {
        actions.setSubmitting(false);
      }
    } catch (error) {
    } finally {
      actions.setSubmitting(false);
    }
  };

  // handler function to skip feedback declared here
  const skipFeedbackHandler = () => {
    navigate('/appointment/list');
  };

  // rendering feedback modal component here
  return (
    <ParentModal onClick={() => {}} hideCloseButton>
      <ModalTitle titleText='submit feedback' />

      <Formik
        initialValues={{ rating: '', comments: '', retain_answer: '' }}
        onSubmit={createFeedbackHandler}
        validationSchema={
          currentUserType === 'client'
            ? clientFeedbackSchema
            : associateFeedbackSchema
        }
      >
        {(props) => (
          <Form className='space-y-3'>
            {currentUserType === 'client' ? (
              <p className='text-base-dark-blue'>
                How was your experience through out the session? Please, let us
                know so that we can improve our service.
              </p>
            ) : (
              <p className='text-base-dark-blue'>
                Please, submit your feedbacks & important notes in the comments.
              </p>
            )}

            <div className='space-y-3'>
              {currentUserType === 'client' ? (
                <div className='space-y-3'>
                  <div id='retain-answer-group'>
                    <label className='block text-base-dark-blue font-medium text-base'>
                      Do you want to retain this associate?{' '}
                      <span className='text-base-red'>*</span>
                    </label>

                    <div
                      role='group'
                      aria-labelledby='retain-answer-group'
                      className='space-y-2 mt-2'
                    >
                      <AcademiaRadioField
                        name='retain_answer'
                        id='yes'
                        label='Yes'
                        value='yes'
                      />

                      <AcademiaRadioField
                        name='retain_answer'
                        id='no'
                        label='No'
                        value='no'
                      />

                      <AcademiaRadioField
                        name='retain_answer'
                        id='not_decided'
                        label='Not Decided Yet'
                        value='undecided'
                      />
                    </div>

                    <motion.label
                      initial={{ y: -20, opacity: 0, display: 'none' }}
                      animate={
                        props.touched.retain_answer &&
                        props.errors.retain_answer
                          ? { y: 0, opacity: 1, display: 'block' }
                          : {
                              y: -20,
                              opacity: 0,
                              transitionEnd: { display: 'none' },
                            }
                      }
                      htmlFor='retain-answer-group'
                      className='block mt-1 text-base-red text-base'
                    >
                      {props.errors.retain_answer}
                    </motion.label>
                  </div>

                  <div>
                    <label className='block text-base-dark-blue font-medium text-base'>
                      Rate Our Associate{' '}
                      <span className='text-base-red'>*</span>
                    </label>

                    <div className='flex items-end gap-2'>
                      <Rating
                        onClick={(value) =>
                          props.setFieldValue('rating', value)
                        }
                        SVGclassName='inline'
                        size={45}
                      />

                      <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={
                          props.values.rating
                            ? { y: 0, opacity: 1 }
                            : { y: 20, opacity: 0 }
                        }
                        className='flex items-center gap-2'
                      >
                        <p className='text-lg font-medium text-base-dark-green'>
                          {props.values.rating}
                          <span className='text-sm font-normal text-base-orange'>
                            /5
                          </span>
                        </p>

                        {+props.values.rating === 5 ? (
                          <SkillBadge badgeLabel='Excellent' color='#845ADF' />
                        ) : +props.values.rating === 4 ? (
                          <SkillBadge badgeLabel='Good' color='#00BCEF' />
                        ) : +props.values.rating === 3 ? (
                          <SkillBadge badgeLabel='Average' color='#1ABC9C' />
                        ) : +props.values.rating === 2 ? (
                          <SkillBadge badgeLabel='Bad' color='#FF8042' />
                        ) : (
                          <SkillBadge badgeLabel='Worst' color='#E6533C' />
                        )}
                      </motion.div>
                    </div>

                    <motion.label
                      initial={{ y: -20, opacity: 0, display: 'none' }}
                      animate={
                        props.touched.rating && props.errors.rating
                          ? { y: 0, opacity: 1, display: 'block' }
                          : {
                              y: -20,
                              opacity: 0,
                              transitionEnd: { display: 'none' },
                            }
                      }
                      htmlFor='rating'
                      className='block mt-1 text-base-red text-base'
                    >
                      {props.errors.rating}
                    </motion.label>
                  </div>
                </div>
              ) : null}

              <AcademiaTextAreaField
                name='comments'
                label='Comments'
                placeholder='Type your feedback here...'
                isRequired
              />

              <div className='flex items-center gap-3'>
                <AcademiaButton
                  type='submit'
                  icon={<TbSquareRoundedPlusFilled className='size-6' />}
                  isLoading={props.isSubmitting}
                  variant='premium'
                  fullWidth
                >
                  Submit
                </AcademiaButton>

                {currentUserType === 'client' ? (
                  <AcademiaButton
                    type='button'
                    onClick={skipFeedbackHandler}
                    icon={<GoSkipFill className='size-6' />}
                    variant='warning'
                    fullWidth
                  >
                    Skip
                  </AcademiaButton>
                ) : null}
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default FeedbackModal;
